import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Tag,
  Calendar,
  Package,
  CheckCircle,
  XCircle,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  Clock
} from 'lucide-react'
import useOfferStore from '@/stores/offerStore'
import useAppConfigStore from '@/stores/appConfigStore'
import { offerService, Offer } from '@/services/offerService'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'

const OfferList = () => {
  const {
    offers,
    loading,
    error,
    setFilters,
    fetchOffers,
    deleteOffer
  } = useOfferStore()
  
  const { systemConfig, availableCurrencies } = useAppConfigStore()
  const navigate = useNavigate()

  const [stats, setStats] = useState<any>(null)

  // Create a mapping of currency code to symbol
  const currencySymbolMap = useMemo(() => {
    const map: Record<string, string> = {}
    availableCurrencies.forEach((currency) => {
      map[currency.code] = currency.symbol || currency.code
    })
    return map
  }, [availableCurrencies])

  useEffect(() => {
    fetchOffers()
    loadStats()
  }, [])

  const loadStats = async () => {
    const statsData = await offerService.getOfferStats()
    setStats(statsData)
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette offre ?')) {
      try {
        await deleteOffer(id)
        loadStats()
      } catch (error) {
        console.error('Error deleting offer:', error)
      }
    }
  }

  const handleSearch = (value: string) => {
    console.log('[DEBUG] OfferList handleSearch called with value:', value)
    setFilters({ search: value })
    // Note: setFilters already calls fetchOffers() internally
  }

  const handleFilterChange = (filters: any[]) => {
    const filterObj: any = {}
    filters.forEach(filter => {
      if (filter.key === 'type' && filter.value) {
        filterObj.type = filter.value
      } else if (filter.key === 'is_active' && filter.value !== 'all') {
        filterObj.is_active = filter.value === 'active'
      }
    })
    
    setFilters(filterObj)
    fetchOffers()
  }

  const handleRefresh = () => {
    fetchOffers()
    loadStats()
  }

  const formatPrice = (price: number | string | null) => {
    if (!price) return 'N/D'
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    if (isNaN(numPrice)) return 'N/D'
    const currencyCode = systemConfig?.default_currency || ''
    const symbol = currencySymbolMap[currencyCode] || currencyCode
    // If symbol is a single character (like €, $, £) place before price, otherwise after
    if (symbol.length === 1 || symbol.length === 2) {
      return `${symbol}${numPrice.toFixed(2)}`
    } else {
      return `${numPrice.toFixed(2)} ${symbol}`
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Non définie'
    return new Date(dateString).toLocaleDateString('fr-FR')
  }

  const getTypeColor = (type: string) => {
    const typeConfig = offerService.getOfferTypes().find(t => t.value === type)
    return typeConfig?.color || 'bg-gray-100 text-gray-800'
  }

  const getTypeLabel = (type: string) => {
    const typeConfig = offerService.getOfferTypes().find(t => t.value === type)
    return typeConfig?.label || type
  }

  // Define columns for DataTable
  const columns = [
    {
      key: 'offer',
      label: 'Offre',
      sortable: true,
      render: (_value: any, row: Offer) => {
        if (!row) return <div className="text-gray-500">N/D</div>;
        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{row.title}</span>
              {(row as any).is_surprise_bag && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                  🎁 Sac surprise
                </Badge>
              )}
            </div>
            <div className="text-sm text-gray-500 truncate max-w-xs">
              {row.description ? `${row.description.substring(0, 60)}...` : 'Aucune description'}
            </div>
          </div>
        );
      }
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (value: string) => (
        <Badge className={getTypeColor(value)}>
          {getTypeLabel(value)}
        </Badge>
      )
    },
    {
      key: 'price',
      label: 'Prix',
      sortable: true,
      render: (_value: any, row: Offer) => {
        if (!row) return <div className="text-sm text-gray-500">N/D</div>;
        // Surprise bag: show fixed price
        if ((row as any).is_surprise_bag && (row as any).surprise_bag_price) {
          return (
            <div>
              <div className="text-sm font-bold text-amber-600">
                {formatPrice((row as any).surprise_bag_price)}
              </div>
              <div className="text-xs text-amber-500">Prix fixe</div>
            </div>
          )
        }
        if (row.discount_price !== null && row.original_price !== null) {
          return (
            <div>
              <div className="text-sm text-gray-900 line-through">
                {formatPrice(row.original_price)}
              </div>
              <div className="text-sm font-medium text-green-600">
                {formatPrice(row.discount_price)}
              </div>
            </div>
          )
        } else if (row.discount_percentage !== null) {
          return (
            <div className="text-sm font-medium text-green-600">
              -{row.discount_percentage}%
            </div>
          )
        }
        return <div className="text-sm text-gray-500">N/D</div>
      }
    },
    {
      key: 'dates',
      label: 'Dates',
      sortable: true,
      render: (_value: any, row: Offer) => {
        if (!row) return <div className="text-gray-500">N/D</div>;
        return (
          <div>
            <div className="flex items-center text-sm text-gray-900">
              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
              {formatDate(row.start_date)}
            </div>
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
              {formatDate(row.end_date)}
            </div>
          </div>
        );
      }
    },
    {
      key: 'preparation_time',
      label: 'Préparation',
      sortable: false,
      render: (_value: any, row: Offer) => {
        if (!row || !row.preparation_time_minutes) return <div className="text-sm text-gray-400">—</div>;
        const mins = row.preparation_time_minutes;
        return (
          <div className="flex items-center gap-1.5 text-sm text-gray-700">
            <Clock className="h-4 w-4 text-gray-400" />
            <span>{mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}min`}</span>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (_value: any, row: Offer) => {
        if (!row) return <div className="text-gray-500">N/D</div>;
        return row.is_active ? (
          <Badge variant="default" className="inline-flex items-center bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-4 w-4 mr-1" />
            Actif
          </Badge>
        ) : (
          <Badge variant="destructive" className="inline-flex items-center">
            <XCircle className="h-4 w-4 mr-1" />
            Inactif
          </Badge>
        );
      }
    }
  ]

  // Define actions for DataTable
  const actions = [
    {
      label: 'Voir',
      icon: <Eye className="h-4 w-4" />,
      onClick: (row: Offer) => navigate(`/offers/${row.offer_id}`),
      variant: 'outline' as const
    },
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: (row: Offer) => navigate(`/offers/${row.offer_id}/edit`),
      variant: 'outline' as const
    },
    {
      label: 'Supprimer',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (row: Offer) => handleDelete(row.offer_id),
      variant: 'destructive' as const
    }
  ]

  // Define filter options
  const filterOptions = [
    {
      key: 'search',
      label: 'Recherche',
      type: 'text' as const,
      placeholder: 'Titre, description...'
    },
    {
      key: 'type',
      label: 'Type',
      type: 'select' as const,
      options: [
        { value: '', label: 'Tous les types' },
        ...offerService.getOfferTypes().map(type => ({
          value: type.value,
          label: type.label
        }))
      ]
    },
    {
      key: 'is_active',
      label: 'Statut',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'active', label: 'Actives' },
        { value: 'inactive', label: 'Inactives' }
      ]
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Offres"
        subtitle="Gérez toutes les offres promotionnelles"
        breadcrumbs={[
          { label: 'Tableau de bord', href: '/' },
          { label: 'Offres', href: '/offers' }
        ]}
        actions={[
          {
            label: 'Actualiser',
            icon: <RefreshCw className="h-5 w-5" />,
            onClick: handleRefresh,
            variant: 'outline' as const
          },
          {
            label: 'Nouvelle offre',
            icon: <Plus className="h-5 w-5" />,
            onClick: () => navigate('/offers/new'),
            variant: 'default' as const
          }
        ]}
      />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Tag className="h-8 w-8 text-primary-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Actives</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <XCircle className="h-8 w-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Expirées</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.expired}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Package className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Types</p>
                  <p className="text-2xl font-bold text-gray-900">{Object.keys(stats.by_type).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FilterBar
        searchPlaceholder="Rechercher des offres..."
        onSearch={handleSearch}
        filters={filterOptions}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        data={offers}
        columns={columns}
        actions={actions}
        loading={loading}
        emptyMessage="Aucune offre trouvée"
      />
    </div>
  )
}

export default OfferList
